/**
 * classifyPm.js — Source de vérité UNIQUE de classification PM (frontend).
 *
 * Sprint refactor/pm-classification-single-source B1 (D1/D2). Voir DECISIONS.
 *
 * D2 — 3 PALIERS STRICTS calés sur les 2 seuils Van Erck (warningMax / alertMax) :
 *   value <= warningMax → 'bon'          → token semanticColors.greenText
 *   value <= alertMax   → 'a_surveiller' → token semanticColors.amberText
 *   value >  alertMax    → 'eleve'        → token semanticColors.redText
 *
 * Bornes `<=` cohérentes avec le code d'alerte backend (`pmAlertService.checkPM`,
 * comparaisons strictes `>` : `avg > alertMax` → alert, `avg > warningMax` → warning).
 * Conséquence : une valeur PILE sur un seuil (25, 40, 50, 80…) appartient au palier
 * INFÉRIEUR — identique côté badge (ici) et côté alerte e-mail (backend).
 *
 * Seuils applicables (D1) : par cheval si fournis (`thresholds {warningMax, alertMax}`,
 * issus d'AlertThreshold), sinon défauts JSON `PM_THRESHOLDS[param]` (source
 * shared/pmThresholds.json copiée dans src/constants/ — cf. parité testée). Le câblage
 * des seuils par cheval sur les sites consommateurs = B2 (pas dans ce sprint).
 *
 * `param` : 'pm1' | 'pm25' | 'pm10'. PM1 est inclus (au-delà des pm25/pm10 pilotables
 * par cheval) pour que la classification 3-paliers couvre tout le PM affiché par
 * AlertSystem — sinon PM1 conserverait l'ancien vocabulaire 4-paliers.
 *
 * Le vocabulaire « Mauvais » / « Dangereux » est SUPPRIMÉ (D2).
 *
 * Mesure absente (null / undefined / NaN / Infinity) → palier 'non_mesure', token
 * neutre : une valeur non mesurée n'est JAMAIS classée « bon » (D-NULL, v10.5.276).
 */
import { PM_THRESHOLDS } from './pmThresholds';

const PM_TOKENS = {
  bon:          'greenText',
  a_surveiller: 'amberText',
  eleve:        'redText',
  non_mesure:   'neutralText',
};

/**
 * Présentation canonique des 3 paliers pour les badges propriétaire (D2) :
 * libellé FR + noms de tokens `semanticColors` (texte AAA + fond pastel).
 * Source unique consommée par SessionListPage, HorseDetail, etc. — évite de
 * redupliquer le vocabulaire « Bon / À surveiller / Élevé » et les tokens.
 */
export const PM_LEVEL_BADGE = Object.freeze({
  bon:          { label: 'Bon',          textToken: 'greenText', bgToken: 'greenBg' },
  a_surveiller: { label: 'À surveiller', textToken: 'amberText', bgToken: 'amberBg' },
  eleve:        { label: 'Élevé',        textToken: 'redText',   bgToken: 'redBg' },
  non_mesure:   { label: 'Non mesuré',   textToken: 'neutralText', bgToken: 'neutralBg' },
});

/**
 * @param {number|null} value  Valeur PM mesurée (µg/m³), null si non mesurée.
 * @param {'pm1'|'pm25'|'pm10'} param  Paramètre PM.
 * @param {{warningMax:number, alertMax:number}} [thresholds]  Seuils par cheval (optionnel).
 * @returns {{ level: 'bon'|'a_surveiller'|'eleve'|'non_mesure',
 *             token: 'greenText'|'amberText'|'redText'|'neutralText' }}
 */
export function classifyPm(value, param, thresholds) {
  const defaults = PM_THRESHOLDS[param];
  if (!defaults) {
    throw new Error(`classifyPm: paramètre PM inconnu « ${param} » (attendu 'pm1' | 'pm25' | 'pm10')`);
  }

  const warningMax = thresholds?.warningMax ?? defaults.warningMax;
  const alertMax   = thresholds?.alertMax   ?? defaults.alertMax;

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return { level: 'non_mesure', token: PM_TOKENS.non_mesure };
  }

  let level;
  if (value <= warningMax)    level = 'bon';
  else if (value <= alertMax) level = 'a_surveiller';
  else                        level = 'eleve';

  return { level, token: PM_TOKENS[level] };
}
